import { Box, Chip, CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FormField from "components/FormField/FormField";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import React, { useMemo, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";

type SelectionChipItem = {
  value: string;
  label?: string;
  selectionKey: string;
  selectionIndex?: number;
};

const AutoCompleteWithSelections: React.FC<{
  field: ControllerRenderProps<any, any>;
  error: string | null | object;
  options: SelectOptions;
  loading: boolean;
  label?: string;
  placeholder?: string;
  variant?: string;
  search?: (v: string) => void;
  disabled?: boolean;
  getOptionDisabled?: (option: SelectOptions[number]) => boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  selectedItemsPlacement?: "top" | "bottom";
  selectedItems?: SelectionChipItem[];
  onSelectOption?: (option: SelectOptions[number]) => void;
  onDeleteSelection?: (selectionKey: string, selectionIndex?: number) => void;
}> = ({
  disabled,
  field,
  error,
  options,
  loading,
  label,
  search,
  placeholder,
  variant = "standard",
  getOptionDisabled,
  disableCloseOnSelect = false,
  disableClearable = false,
  selectedItemsPlacement = "bottom",
  selectedItems: selectedItemsOverride,
  onSelectOption,
  onDeleteSelection,
}) => {
  const [inputValue, setInputValue] = useState("");

  const extraProps = {
    ...(search ? { filterOptions: (x) => x } : {}),
    ...(options?.every((x) => !!x?.category)
      ? {
          groupBy: (option: any) => option.category,
        }
      : {}),
  };

  const selectedItems = useMemo(() => {
    if (selectedItemsOverride) {
      return selectedItemsOverride;
    }

    return (field.value || []).map((val: any, index: number) => {
      const matchedOption = options?.find((opt) => opt.value === val) || { value: val, label: val };

      return {
        ...matchedOption,
        selectionKey: `${matchedOption.value}-${index}`,
        selectionIndex: index,
      };
    });
  }, [field.value, options, selectedItemsOverride]);

  const selectedChips = useMemo(() => {
    if (selectedItems.length === 0) {
      return null;
    }

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap="8px"
        mb={selectedItemsPlacement === "top" ? "8px" : 0}
      >
        {selectedItems.map((item: any) => (
          <Chip
            key={item.selectionKey}
            label={item.label ?? item.value}
            onDelete={() =>
              onDeleteSelection
                ? onDeleteSelection(item.selectionKey, item.selectionIndex)
                : field.onChange(
                    (field.value || []).filter(
                      (_: unknown, itemIndex: number) => itemIndex !== item.selectionIndex
                    )
                  )
            }
          />
        ))}
      </Box>
    );
  }, [field, onDeleteSelection, selectedItems, selectedItemsPlacement]);

  return (
    <>
      {selectedItemsPlacement === "top" && selectedChips}
      <Autocomplete
        {...field}
        {...extraProps}
        multiple
        disableCloseOnSelect={disableCloseOnSelect}
        disableClearable={disableClearable}
        loading={loading}
        options={options ?? []}
        value={[]}
        inputValue={inputValue}
        openOnFocus
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue ?? "");
            if (search) {
              search(newInputValue ?? "");
            }
            return;
          }

          if (reason === "clear") {
            setInputValue("");
            if (search) {
              search("");
            }
            return;
          }

          if (reason === "reset") {
            return;
          }

          setInputValue(newInputValue ?? "");
          if (search) {
            search(newInputValue ?? "");
          }
        }}
        onChange={(e, value, reason, details) => {
          if (reason === "selectOption" && details?.option) {
            if (onSelectOption) {
              onSelectOption(details.option);
            } else {
              field.onChange([...(field.value || []), details.option.value]);
            }
            setInputValue("");
            if (search) {
              search("");
            }
            return;
          }

          if (reason === "clear") {
            field.onChange([]);
          }
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={`${option.label}-${option.value}`}>
              {option.label ?? ""}
            </li>
          );
        }}
        getOptionLabel={(option) => {
          if (option === "" || option === null || option === undefined) {
            return "";
          }
          if (typeof option === "string") {
            return options?.find((opt) => opt.value === option)?.label ?? "";
          }
          return option.label || "";
        }}
        getOptionDisabled={getOptionDisabled}
        renderTags={() => null}
        renderInput={(params) => (
          <FormField
            {...params}
            disabled={disabled}
            inputProps={{
              ...params.inputProps,
              disabled,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            onChange={(e) => {
              params.inputProps.onChange(e);
            }}
            label={label}
            variant={variant}
            error={error}
            placeholder={placeholder}
          />
        )}
      />
      {selectedItemsPlacement === "bottom" && selectedChips}
    </>
  );
};

export default AutoCompleteWithSelections;
